
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  CreditCard,
  AlertCircle,
  Calendar,
  ArrowRight,
  FileText,
  ChevronDown,
  Building,
  CheckSquare,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
} from "recharts";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Toggle } from "@/components/ui/toggle";

interface CreditTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: "credit" | "debit";
  reference: string;
}

interface Invoice {
  id: string;
  refNum: string;
  date: string;
  itemCount: number;
  totalAmount: number;
  daysToPay: number;
  paymentType: "Online Payment" | "Seller Credit" | "ANGKASA Credit";
  creditProvider: string;
  status: "paid" | "pending" | "overdue";
}

interface SellerCredit {
  sellerName: string;
  totalLimit: number;
  usedCredit: number;
  availableCredit: number;
  dueDate: string;
  daysUntilDue: number;
  overdue?: boolean;
}

const CreditSummary: React.FC = () => {
  const [selectedSeller, setSelectedSeller] = useState<string>("ABC Supplies");
  const [showEligibleOnly, setShowEligibleOnly] = useState<boolean>(false);
  
  const sellerCredits: SellerCredit[] = [
    {
      sellerName: "ABC Supplies",
      totalLimit: 5000,
      usedCredit: 3200,
      availableCredit: 1800,
      dueDate: "2025-05-18",
      daysUntilDue: 6,
    },
    {
      sellerName: "XYZ Wholesaler",
      totalLimit: 8000,
      usedCredit: 7900,
      availableCredit: 100,
      dueDate: "2025-05-15",
      daysUntilDue: 3,
      overdue: false,
    }
  ];
  
  const angkasaCredit = {
    coopName: "KoperasiMart Sdn Bhd",
    totalLimit: 15000,
    usedCredit: 6000,
    availableCredit: 9000,
    dueDate: "2025-06-05",
    daysUntilDue: 24,
  };
  
  const currentSellerCredit = sellerCredits.find(credit => credit.sellerName === selectedSeller) || sellerCredits[0];
  
  const invoices: Invoice[] = [
    {
      id: "INV-123456",
      refNum: "REF-A12345",
      date: "2025-05-10",
      itemCount: 12,
      totalAmount: 2350.75,
      daysToPay: 15,
      paymentType: "Seller Credit",
      creditProvider: "ABC Supplies",
      status: "pending"
    },
    {
      id: "INV-123457",
      refNum: "REF-A12346",
      date: "2025-05-08",
      itemCount: 8,
      totalAmount: 1230.50,
      daysToPay: 5,
      paymentType: "ANGKASA Credit",
      creditProvider: "ANGKASA",
      status: "pending"
    },
    {
      id: "INV-123458",
      refNum: "REF-A12347",
      date: "2025-05-05",
      itemCount: 15,
      totalAmount: 3120.45,
      daysToPay: 0,
      paymentType: "Online Payment",
      creditProvider: "Cash",
      status: "paid"
    },
    {
      id: "INV-123459",
      refNum: "REF-A12348",
      date: "2025-05-01",
      itemCount: 6,
      totalAmount: 850.30,
      daysToPay: -3,
      paymentType: "Seller Credit",
      creditProvider: "XYZ Wholesaler",
      status: "overdue"
    }
  ];
  
  const filteredInvoices = showEligibleOnly 
    ? invoices.filter(invoice => 
        invoice.paymentType === "ANGKASA Credit" || 
        invoice.paymentType === "Seller Credit") 
    : invoices;

  const creditUsageData = [
    { month: "Jan", sellerUsage: 4500, angkasaUsage: 3500 },
    { month: "Feb", sellerUsage: 5200, angkasaUsage: 4000 },
    { month: "Mar", sellerUsage: 4800, angkasaUsage: 5200 },
    { month: "Apr", sellerUsage: 6100, angkasaUsage: 5800 },
    { month: "May", sellerUsage: 7500, angkasaUsage: 6000 },
  ];

  const getStatusColor = (status: string, daysToPay: number) => {
    if (status === 'paid') return "text-green-600";
    if (status === 'overdue' || daysToPay < 0) return "text-red-600";
    if (daysToPay <= 7) return "text-amber-600";
    return "text-blue-600";
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Credit Summary</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-6">
          {/* Seller Credit Card */}
          <Card className="border-green-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-green-700" />
                  Seller Credit
                </div>
                <div className="relative inline-block text-left">
                  <button
                    className="inline-flex justify-center items-center w-full px-3 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 focus:outline-none"
                    onClick={() => {}}
                  >
                    {selectedSeller} <ChevronDown className="ml-2 h-4 w-4" />
                  </button>
                  {/* Dropdown would go here in a full implementation */}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Available Credit</div>
                  <div className="text-3xl font-bold">RM {currentSellerCredit.availableCredit.toFixed(2)}</div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Credit Used: RM {currentSellerCredit.usedCredit.toFixed(2)}</span>
                    <span>{(currentSellerCredit.usedCredit / currentSellerCredit.totalLimit * 100).toFixed(0)}%</span>
                  </div>
                  <Progress value={currentSellerCredit.usedCredit / currentSellerCredit.totalLimit * 100} className="h-2" />
                  <div className="flex justify-between text-sm mt-1">
                    <span>Total Credit Limit</span>
                    <span>RM {currentSellerCredit.totalLimit.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className={`p-3 rounded-lg flex items-center space-x-2 ${currentSellerCredit.overdue ? 'bg-red-50' : 'bg-amber-50'}`}>
                  <Calendar className={`h-5 w-5 ${currentSellerCredit.overdue ? 'text-red-600' : 'text-amber-600'}`} />
                  <div>
                    <p className="text-sm font-medium">{currentSellerCredit.overdue ? 'Payment Overdue' : 'Payment Due'}</p>
                    <p className="text-xs">{currentSellerCredit.dueDate} ({currentSellerCredit.daysUntilDue} days)</p>
                  </div>
                </div>
                
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  Make Payment
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* ANGKASA Credit Card */}
          <Card className="border-green-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Building className="h-5 w-5 mr-2 text-green-700" />
                ANGKASA Credit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-sm font-medium mb-2">Coop Name</div>
                <div className="text-base mb-4">{angkasaCredit.coopName}</div>
                
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Available Credit</div>
                  <div className="text-3xl font-bold">RM {angkasaCredit.availableCredit.toFixed(2)}</div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Credit Used: RM {angkasaCredit.usedCredit.toFixed(2)}</span>
                    <span>{(angkasaCredit.usedCredit / angkasaCredit.totalLimit * 100).toFixed(0)}%</span>
                  </div>
                  <Progress value={angkasaCredit.usedCredit / angkasaCredit.totalLimit * 100} className="h-2" />
                  <div className="flex justify-between text-sm mt-1">
                    <span>Total Credit Limit</span>
                    <span>RM {angkasaCredit.totalLimit.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="bg-amber-50 p-3 rounded-lg flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium">Payment Due</p>
                    <p className="text-xs">{angkasaCredit.dueDate} ({angkasaCredit.daysUntilDue} days)</p>
                  </div>
                </div>
                
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  Make Payment
                </Button>
                
                <Button variant="outline" className="w-full">
                  Request Credit Increase <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <Card className="border-green-100 mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-700" />
                Credit Usage History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={creditUsageData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5F5DA" />
                    <XAxis dataKey="month" stroke="#8CC84B" />
                    <YAxis stroke="#8CC84B" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="sellerUsage"
                      name="Seller Credit (RM)"
                      stroke="#4D9E41"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="angkasaUsage"
                      name="ANGKASA Credit (RM)"
                      stroke="#8884d8"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-100">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-green-700" />
                  Invoice List
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="eligible-products"
                    checked={showEligibleOnly}
                    onCheckedChange={setShowEligibleOnly}
                  />
                  <Label htmlFor="eligible-products" className="text-sm">
                    Eligible Products Only
                  </Label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-green-50">
                      <TableHead>Ref Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead>Amount (RM)</TableHead>
                      <TableHead>Payment Type</TableHead>
                      <TableHead>Credit Provider</TableHead>
                      <TableHead>Days to Pay</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.refNum}</TableCell>
                        <TableCell>{invoice.date}</TableCell>
                        <TableCell className="text-center">{invoice.itemCount}</TableCell>
                        <TableCell>{invoice.totalAmount.toFixed(2)}</TableCell>
                        <TableCell>{invoice.paymentType}</TableCell>
                        <TableCell>{invoice.creditProvider}</TableCell>
                        <TableCell>
                          <span className={getStatusColor(invoice.status, invoice.daysToPay)}>
                            {invoice.status === 'paid' ? (
                              'Paid'
                            ) : invoice.daysToPay < 0 ? (
                              `Overdue (${Math.abs(invoice.daysToPay)} days)`
                            ) : (
                              `${invoice.daysToPay} days`
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" size="sm">
                              View Invoice
                            </Button>
                            {invoice.status !== 'paid' && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                Make Payment
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreditSummary;
